/**
 * Core Flow Engine implementation
 * Manages flow execution, state, and events
 * Adapted for React environment with TypeScript patterns
 */

import { logger } from "../../utils/logger";

import { TypedEventEmitter } from "./eventEmitter";
import { FlowLoader } from "./flowLoader";
import type {
  BuiltInVariables,
  Flow,
  FlowEngineOptions,
  FlowEventHandler,
  FlowEventMap,
  FlowEventType,
  FlowState,
  Step,
} from "./types";
import { FlowError } from "./types";
import { VariableResolver } from "./variableResolver";

// Create component logger for flow engine
const flowEngineLogger = logger.withComponent("FlowEngine");

/**
 * Core flow execution engine
 *
 * Provides:
 * - Flow loading and validation
 * - Step-by-step execution with state management
 * - Variable interpolation and storage
 * - Event-driven architecture for UI integration
 * - Safety features (max steps, timeouts, circular reference detection)
 *
 * @example
 * ```typescript
 * const engine = new FlowEngine({
 *   maxSteps: 100,
 *   maxExecutionTime: 300000
 * });
 *
 * // Subscribe to events
 * engine.on('stepChanged', (event) => {
 *   flowEngineLogger.info('New step', { stepId: event.to.id });
 * });
 *
 * // Load and start a flow
 * await engine.loadFlow('wifi-help');
 * engine.start();
 * ```
 */
export class FlowEngine {
  // Internal state - managed as plain object since UI updates via events
  private state: FlowState = {
    flow: null,
    currentStep: null,
    variables: {},
    stepCount: 0,
    startTime: 0,
    status: "idle",
    history: [],
  };

  private flow: Flow | null = null;
  private eventEmitter = new TypedEventEmitter();
  private options: Required<FlowEngineOptions>;
  private executionTimer: number | null = null;

  constructor(options: FlowEngineOptions = {}) {
    this.options = {
      initialVariables: {},
      maxSteps: 50,
      maxExecutionTime: 300000, // 5 minutes
      ...options,
    };

    // Initialize with provided variables
    this.state.variables = { ...this.options.initialVariables };
  }

  /**
   * Load a flow by ID
   *
   * @param flowId - Unique flow identifier
   * @throws {FlowError} If flow loading fails
   */
  async loadFlow(flowId: string): Promise<void> {
    try {
      this.flow = await FlowLoader.loadFlow(flowId);
      this.state.flow = this.flow;

      // Override default limits if specified in flow
      if (this.flow.maxSteps !== undefined) {
        this.options.maxSteps = this.flow.maxSteps;
      }
      if (this.flow.maxExecutionTime !== undefined) {
        this.options.maxExecutionTime = this.flow.maxExecutionTime;
      }

      // Set built-in flowId variable
      this.setVariable("flowId", flowId);
    } catch (error) {
      this.emitError(error);
      throw error;
    }
  }

  /**
   * Load a flow from URL (for CDN flows)
   *
   * @param url - URL to flow JSON file
   */
  async loadFlowFromUrl(url: string): Promise<void> {
    try {
      this.flow = await FlowLoader.loadFlowFromUrl(url);
      this.state.flow = this.flow;

      // Extract flow ID from URL or use URL as ID
      const flowId = url.split("/").pop()?.replace(".json", "") || url;
      this.setVariable("flowId", flowId);

      // Override default limits if specified in flow
      if (this.flow.maxSteps !== undefined) {
        this.options.maxSteps = this.flow.maxSteps;
      }
      if (this.flow.maxExecutionTime !== undefined) {
        this.options.maxExecutionTime = this.flow.maxExecutionTime;
      }
    } catch (error) {
      this.emitError(error);
      throw error;
    }
  }

  /**
   * Start or restart flow execution
   *
   * @throws {FlowError} If no flow is loaded
   */
  start(): void {
    if (!this.flow) {
      throw new FlowError("invalidFlow", "No flow loaded");
    }

    this.reset();
    this.state.status = "running";
    this.state.startTime = Date.now();

    // Emit flow started event
    this.eventEmitter.emit("flowStarted", {
      flow: this.flow,
    });

    // Set execution timer (use setTimeout instead of window.setTimeout for better compatibility)
    this.executionTimer = setTimeout(() => {
      this.emitError(
        new FlowError(
          "executionTimeout",
          `Execution timeout: ${this.options.maxExecutionTime}ms`
        )
      );
    }, this.options.maxExecutionTime) as unknown as number;

    // Go to first step
    if (this.flow.steps.length > 0) {
      this.transitionToStep(this.flow.steps[0]);
    }
  }

  /**
   * Reset engine to initial state
   */
  reset(): void {
    if (this.executionTimer) {
      clearTimeout(this.executionTimer);
      this.executionTimer = null;
    }

    this.state.currentStep = null;
    this.state.variables = { ...this.options.initialVariables };
    this.state.stepCount = 0;
    this.state.startTime = 0;
    this.state.status = "idle";
    this.state.history = [];

    // Restore flowId if flow is loaded
    if (this.flow) {
      this.setVariable("flowId", this.flow.id);
    }
  }

  /**
   * Handle chip selection from a chips step
   *
   * @param chipId - ID of the selected chip
   */
  selectChip(chipId: string): void {
    if (!this.state.currentStep || this.state.currentStep.type !== "chips") {
      flowEngineLogger.warn(
        "selectChip called but current step is not a chips step"
      );
      return;
    }

    const chip = this.state.currentStep.chips?.find(c => c.id === chipId);
    if (!chip) {
      this.emitError(new FlowError("invalidChip", `Chip not found: ${chipId}`));
      return;
    }

    // Process chip directives
    if (chip.setVars) {
      Object.entries(chip.setVars).forEach(([name, value]) => {
        this.setVariable(name, value);
      });
    }

    if (chip.syncStoryline) {
      this.eventEmitter.emit("storylineSync", {
        variables: chip.syncStoryline,
      });
    }

    // Handle chip action or transformToUser
    if (chip.action || chip.transformToUser) {
      this.eventEmitter.emit("chipAction", {
        action: chip.action || "",
        data: { chip, transformToUser: chip.transformToUser },
      });

      // Special handling for closeChat action
      if (chip.action === "closeChat") {
        this.complete();
        return;
      }
    }

    // Reset history on user interaction to allow revisiting steps
    this.state.history = [this.state.currentStep?.id].filter(
      Boolean
    ) as string[];

    // Navigate to next step
    if (chip.next) {
      this.goToNext(chip.next);
    }
  }

  /**
   * Navigate to a specific step by ID
   *
   * @param stepId - ID of step to navigate to
   */
  goToNext(stepId: string): void {
    if (!this.flow) {
      flowEngineLogger.warn("goToNext called but no flow is loaded");
      return;
    }

    // Check step limits
    if (this.state.stepCount >= this.options.maxSteps) {
      this.emitError(
        new FlowError(
          "maxStepsExceeded",
          `Maximum steps exceeded: ${this.options.maxSteps}`
        )
      );
      return;
    }

    // Find the step
    const nextStep = FlowLoader.findStep(this.flow, stepId);
    if (!nextStep) {
      this.emitError(
        new FlowError("invalidStep", `Invalid step reference: ${stepId}`)
      );
      return;
    }

    // Check for circular reference
    if (this.state.history.includes(stepId)) {
      this.emitError(
        new FlowError(
          "circularReference",
          `Circular reference detected: ${stepId}`
        )
      );
      return;
    }

    this.transitionToStep(nextStep);
  }

  /**
   * Set a variable value and emit change event
   *
   * @param name - Variable name
   * @param value - Variable value
   */
  setVariable(name: string, value: any): void {
    this.state.variables[name] = value;
    this.eventEmitter.emit("variableSet", { name, value });
  }

  /**
   * Get a variable value
   *
   * @param name - Variable name
   * @returns Variable value or undefined
   */
  getVariable(name: string): any {
    return this.state.variables[name];
  }

  /**
   * Set multiple variables at once
   *
   * @param variables - Object containing variable key-value pairs
   */
  setVariables(variables: Record<string, any>): void {
    Object.entries(variables).forEach(([name, value]) => {
      this.setVariable(name, value);
    });
  }

  /**
   * Get current step with interpolated content
   *
   * @returns Current step with variables resolved or null
   */
  getCurrentStep(): Step | null {
    if (!this.state.currentStep) return null;

    // Return a copy with interpolated values
    return VariableResolver.interpolateObject(
      this.state.currentStep,
      this.state.variables
    );
  }

  /**
   * Get full engine state
   *
   * @returns Complete flow state including interpolated current step
   */
  getState(): FlowState {
    return {
      ...this.state,
      currentStep: this.getCurrentStep(),
    };
  }

  /**
   * Get flow information
   *
   * @returns Current flow or null
   */
  getFlow(): Flow | null {
    return this.flow;
  }

  /**
   * Get all variables
   *
   * @returns Copy of variables object
   */
  getVariables(): Record<string, any> {
    return { ...this.state.variables };
  }

  /**
   * Subscribe to events
   *
   * @param event - Event type to listen for
   * @param handler - Event handler function
   */
  on<T extends FlowEventType>(event: T, handler: FlowEventHandler<T>): void {
    this.eventEmitter.on(event, handler);
  }

  /**
   * Unsubscribe from events
   *
   * @param event - Event type to stop listening for
   * @param handler - Event handler to remove
   */
  off<T extends FlowEventType>(event: T, handler: FlowEventHandler<T>): void {
    this.eventEmitter.off(event, handler);
  }

  /**
   * Subscribe to an event for one-time execution
   *
   * @param event - Event type to listen for once
   * @param handler - Event handler function
   */
  once<T extends FlowEventType>(event: T, handler: FlowEventHandler<T>): void {
    this.eventEmitter.once(event, handler);
  }

  /**
   * Emit an event (exposed for step components)
   *
   * @param event - Event type to emit
   * @param data - Event data
   */
  emit<T extends FlowEventType>(event: T, data: FlowEventMap[T]): void {
    this.eventEmitter.emit(event, data);
  }

  /**
   * Public API for step components to update built-in variables
   *
   * @param name - Built-in variable name
   * @param value - Variable value
   */
  updateBuiltInVariable(name: keyof BuiltInVariables, value: any): void {
    this.setVariable(name, value);
  }

  /**
   * Handle text input completion
   *
   * @param text - User text input
   */
  handleTextInput(text: string): void {
    this.updateBuiltInVariable("lastUserText", text);
    this.eventEmitter.emit("inputComplete", { type: "text", value: text });

    // Reset history on user interaction to allow revisiting steps
    this.state.history = [this.state.currentStep?.id].filter(
      Boolean
    ) as string[];

    // Auto-advance if current step has next
    if (this.state.currentStep?.next) {
      this.goToNext(this.state.currentStep.next);
    }
  }

  /**
   * Handle image input completion
   *
   * @param imageUrl - Uploaded image URL
   * @param file - Original file object (optional)
   */
  handleImageInput(imageUrl: string, file?: File): void {
    this.updateBuiltInVariable("lastImage", imageUrl);
    this.eventEmitter.emit("inputComplete", {
      type: "image",
      value: imageUrl,
      file,
    });

    // Store in imageVar if specified
    if (this.state.currentStep?.imageVar) {
      this.setVariable(this.state.currentStep.imageVar, imageUrl);
    }

    // Reset history on user interaction to allow revisiting steps
    this.state.history = [this.state.currentStep?.id].filter(
      Boolean
    ) as string[];

    // Auto-advance if current step has next
    if (this.state.currentStep?.next) {
      this.goToNext(this.state.currentStep.next);
    }
  }

  /**
   * Handle error navigation for API steps
   */
  handleStepError(error?: any): void {
    if (!this.state.currentStep?.errorNext) {
      this.emitError(
        new FlowError(
          "missingErrorHandler",
          "No error handler defined for step",
          error
        )
      );
      return;
    }

    this.goToNext(this.state.currentStep.errorNext);
  }

  /**
   * Check if flow is running
   *
   * @returns True if flow is currently running
   */
  isRunning(): boolean {
    return this.state.status === "running";
  }

  /**
   * Check if flow is completed
   *
   * @returns True if flow has completed
   */
  isCompleted(): boolean {
    return this.state.status === "completed";
  }

  /**
   * Check if flow has an error
   *
   * @returns True if flow is in error state
   */
  hasError(): boolean {
    return this.state.status === "error";
  }

  /**
   * Dispose of the engine and clean up resources
   */
  dispose(): void {
    if (this.executionTimer) {
      clearTimeout(this.executionTimer);
      this.executionTimer = null;
    }
    this.eventEmitter.removeAllListeners();
    this.flow = null;
    this.state = {
      flow: null,
      currentStep: null,
      variables: {},
      stepCount: 0,
      startTime: 0,
      status: "idle",
      history: [],
    };
  }

  /**
   * Internal: Transition to a new step
   *
   * @private
   */
  private transitionToStep(step: Step): void {
    const previousStep = this.state.currentStep;

    // Check for unsupported step types
    if (step.type === "logic") {
      this.emitError(
        new FlowError(
          "unsupportedStepType",
          `Unsupported step type: ${step.type}`
        )
      );
      return;
    }

    // Handle skipIfExists logic
    if (
      step.skipIfExists &&
      step.storeAs &&
      this.getVariable(step.storeAs) !== undefined
    ) {
      flowEngineLogger.debug(
        `Skipping step ${step.id} because variable ${step.storeAs} already exists`
      );
      if (step.next) {
        this.goToNext(step.next);
      }
      return;
    }

    // Update state
    this.state.currentStep = step;
    this.state.stepCount++;
    this.state.history.push(step.id);

    // Update built-in variables
    this.setVariable("currentStep", step.id);

    // Process step directives
    if (step.syncStoryline) {
      this.eventEmitter.emit("storylineSync", {
        variables: step.syncStoryline,
      });
    }

    // Set step variables
    if (step.variables) {
      Object.entries(step.variables).forEach(([name, value]) => {
        this.setVariable(name, value);
      });
    }

    // Emit step changed event
    this.eventEmitter.emit("stepChanged", {
      from: previousStep,
      to: step,
    });

    // Handle step-specific behavior
    this.handleStepType(step);
  }

  /**
   * Handle step-type specific behavior
   *
   * @private
   */
  private handleStepType(step: Step): void {
    switch (step.type) {
      case "system":
        // Auto-advance for system steps with next (but no chips)
        if (step.next && !step.chips) {
          const delay = step.delayMs || 1000; // Default 1 second delay
          setTimeout(() => {
            if (this.state.currentStep?.id === step.id && step.next) {
              this.goToNext(step.next);
            }
          }, delay);
        }
        break;

      case "textInput":
        this.eventEmitter.emit("inputRequested", {
          type: "text",
          config: {
            placeholder: step.placeholder,
            validation: step.validation,
            required: step.required,
          },
          step: step,
        });
        break;

      case "imageInput":
        this.eventEmitter.emit("inputRequested", {
          type: "image",
          config: {
            validation: step.validation,
            required: step.required,
          },
          step: step,
        });
        break;

      case "chips":
        // No special handling needed - UI will render chips
        break;

      case "llm":
      case "vision":
      case "imageEdit":
      case "helpdesk":
        // These steps require external API calls
        // The UI component should handle the API call and then call handleStepError or goToNext
        break;

      default:
        flowEngineLogger.warn(`Unknown step type: ${step.type}`);
    }
  }

  /**
   * Internal: Emit error event
   *
   * @private
   */
  private emitError(error: any): void {
    const flowError =
      error instanceof FlowError
        ? error
        : new FlowError("error", error.message || "Unknown error", error);

    this.state.status = "error";
    this.eventEmitter.emit("error", {
      type: flowError.type,
      message: flowError.message,
      details: flowError.details,
    });
  }

  /**
   * Stop the flow execution
   */
  stop(): void {
    this.state.status = "idle";
    this.state.currentStep = null;
    if (this.state.flow) {
      this.eventEmitter.emit("flowFinished", {
        flow: this.state.flow,
      });
    }
  }

  /**
   * Proceed to the next step with optional user input
   */
  async next(userInput?: any): Promise<void> {
    if (!this.state.currentStep) {
      throw new FlowError("no_current_step", "No current step to proceed from");
    }

    // Store user input if provided
    if (userInput !== undefined) {
      if (typeof userInput === "string") {
        this.setVariable("lastUserText", userInput);
      }
      // Handle other input types as needed
    }

    // Navigate to the next step
    if (this.state.currentStep.next) {
      await this.goToNext(this.state.currentStep.next);
    } else {
      // If no next step, complete the flow
      this.complete();
    }
  }

  /**
   * Complete the flow execution
   */
  complete(): void {
    this.state.status = "completed";
    this.eventEmitter.emit("flowCompleted", {
      finalStep: this.state.currentStep!,
    });
    if (this.state.flow) {
      this.eventEmitter.emit("flowFinished", {
        flow: this.state.flow,
      });
    }
    if (this.executionTimer) {
      clearTimeout(this.executionTimer);
      this.executionTimer = null;
    }
  }
}
